import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { mapBackgroundImageAndGradient } from '../../util/background.js';
import { interactiveState, interactiveWithinState, INTERACTIVE_WITHIN_CLASS } from '../../util/interactiveState.js';
import { mapGradient } from '../../util/mappers.util.js';
import { getSpacing } from '../../util/spacing.js';
import { selectToken, selectTokens } from '../../util/tokenSelector.js';
import { toStyled } from '@drone-js/media-query';
import { useExperimentContext } from '../../contexts/session/Experiment.js';
import { useDirectionContext } from '../../contexts/session/Locale.js';
import { useThemeContext } from '../../contexts/session/Theme.js';
import { BoxStyled } from './Box.styled.js';
import { BoxExperimentalCss as Box$1 } from './BoxExperimentalCss.js';

var _excluded = ["alignContent", "alignItems", "alignSelf", "aspectRatio", "backgroundColor", "backgroundImage", "backgroundPosition", "backgroundRepeat", "backgroundSize", "borderColor", "borderRadius", "borderStyle", "borderWidth", "bottom", "boxShadow", "boxSizing", "clipPath", "cursor", "display", "end", "filter", "flex", "flexBasis", "flexDirection", "flexGrow", "flexShrink", "flexWrap", "float", "gap", "rowGap", "columnGap", "DEPRECATED_gap", "gridAutoColumns", "gridAutoFlow", "gridAutoRows", "height", "justifyContent", "left", "listStyle", "maskImage", "maxHeight", "maxWidth", "minHeight", "minWidth", "opacity", "order", "outlineColor", "outlineStyle", "overflow", "overflowScrolling", "overflowX", "overflowY", "pointerEvents", "position", "right", "scrollSnapAlign", "scrollSnapStop", "scrollSnapType", "start", "textAlign", "textColor", "textDecoration", "textOverflow", "top", "transform", "transition", "userSelect", "verticalAlign", "visibility", "whiteSpace", "width", "wordBreak", "zIndex", "m", "mb", "me", "ms", "mt", "mx", "my", "p", "pb", "pe", "ps", "pt", "px", "py", "as", "children", "disabled", "empty", "hideScrollbar", "interactive", "interactiveChild", "interactiveWithin", "safeArea", "type", "value"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var propTransform = function propTransform(props, theme, direction) {
  var _mapGradient, _selectToken;
  var alignContent = props.alignContent,
    alignItems = props.alignItems,
    alignSelf = props.alignSelf,
    aspectRatio = props.aspectRatio,
    backgroundColor = props.backgroundColor,
    backgroundImage = props.backgroundImage,
    backgroundPosition = props.backgroundPosition,
    backgroundRepeat = props.backgroundRepeat,
    backgroundSize = props.backgroundSize,
    borderColor = props.borderColor,
    borderRadius = props.borderRadius,
    borderStyle = props.borderStyle,
    borderWidth = props.borderWidth,
    bottom = props.bottom,
    boxShadow = props.boxShadow,
    boxSizing = props.boxSizing,
    clipPath = props.clipPath,
    cursor = props.cursor,
    display = props.display,
    end = props.end,
    filter = props.filter,
    flex = props.flex,
    flexBasis = props.flexBasis,
    flexDirection = props.flexDirection,
    flexGrow = props.flexGrow,
    flexShrink = props.flexShrink,
    flexWrap = props.flexWrap,
    float = props.float,
    fontFamily = props.fontFamily,
    gap = props.gap,
    rowGap = props.rowGap,
    columnGap = props.columnGap,
    DEPRECATED_gap = props.DEPRECATED_gap,
    gridAutoColumns = props.gridAutoColumns,
    gridAutoFlow = props.gridAutoFlow,
    gridAutoRows = props.gridAutoRows,
    height = props.height,
    justifyContent = props.justifyContent,
    left = props.left,
    listStyle = props.listStyle,
    maskImage = props.maskImage,
    maxHeight = props.maxHeight,
    maxWidth = props.maxWidth,
    minHeight = props.minHeight,
    minWidth = props.minWidth,
    opacity = props.opacity,
    order = props.order,
    outlineColor = props.outlineColor,
    outlineStyle = props.outlineStyle,
    overflow = props.overflow,
    overflowX = props.overflowX,
    overflowY = props.overflowY,
    pointerEvents = props.pointerEvents,
    position = props.position,
    right = props.right,
    scrollSnapAlign = props.scrollSnapAlign,
    scrollSnapStop = props.scrollSnapStop,
    scrollSnapType = props.scrollSnapType,
    start = props.start,
    textAlign = props.textAlign,
    textColor = props.textColor,
    textDecoration = props.textDecoration,
    textOverflow = props.textOverflow,
    top = props.top,
    transform = props.transform,
    transition = props.transition,
    userSelect = props.userSelect,
    verticalAlign = props.verticalAlign,
    visibility = props.visibility,
    whiteSpace = props.whiteSpace,
    width = props.width,
    wordBreak = props.wordBreak,
    zIndex = props.zIndex;
  var leftWithDirection = direction === 'ltr' ? start : end;
  var rightWithDirection = direction === 'ltr' ? end : start;
  var floatWithDirection;
  if (float === 'start') {
    floatWithDirection = direction === 'ltr' ? 'left' : 'right';
  }
  if (float === 'end') {
    floatWithDirection = direction === 'ltr' ? 'right' : 'left';
  }
  return {
    $css: _objectSpread(_objectSpread({}, mapBackgroundImageAndGradient(direction, theme, backgroundColor, backgroundImage)), {}, {
      backgroundColor: selectToken(theme.background, backgroundColor),
      backgroundPosition: backgroundPosition,
      backgroundRepeat: backgroundRepeat,
      backgroundSize: backgroundSize,
      borderColor: selectToken(theme.border, borderColor),
      borderRadius: selectTokens(theme.radius, borderRadius, 4),
      borderStyle: borderStyle,
      borderWidth: selectTokens(theme.borderwidth, borderWidth, 4),
      boxShadow: selectToken(theme.shadow, boxShadow),
      color: textColor !== 'inherit' ? selectToken(theme.text, textColor) : textColor,
      fill: textColor !== 'inherit' ? selectToken(theme.text, textColor) : textColor,
      filter: filter,
      outlineColor: outlineColor !== 'transparent' ? selectToken(theme.border, outlineColor) : outlineColor,
      outlineStyle: outlineStyle,
      // FontProps
      fontFamily: fontFamily,
      // DisplayProps
      cursor: cursor,
      display: display,
      listStyle: listStyle,
      maskImage: (_mapGradient = mapGradient(maskImage, direction, theme)) !== null && _mapGradient !== void 0 ? _mapGradient : maskImage,
      opacity: opacity,
      overflow: overflow,
      overflowX: overflowX,
      overflowY: overflowY,
      pointerEvents: pointerEvents,
      textAlign: textAlign,
      textOverflow: textOverflow,
      userSelect: userSelect,
      verticalAlign: verticalAlign,
      visibility: visibility,
      whiteSpace: whiteSpace,
      wordBreak: wordBreak,
      // FlexboxProps
      alignContent: alignContent,
      alignItems: alignItems,
      alignSelf: alignSelf,
      flex: flex,
      flexBasis: flexBasis,
      flexDirection: flexDirection,
      flexGrow: flexGrow,
      flexShrink: flexShrink,
      flexWrap: flexWrap,
      gap: (_selectToken = selectToken(theme.spacing, gap)) !== null && _selectToken !== void 0 ? _selectToken : DEPRECATED_gap,
      rowGap: selectToken(theme.spacing, rowGap),
      columnGap: selectToken(theme.spacing, columnGap),
      justifyContent: justifyContent,
      order: order,
      // GridProps
      gridAutoColumns: gridAutoColumns,
      gridAutoFlow: gridAutoFlow,
      gridAutoRows: gridAutoRows,
      // ClipProps
      clipPath: clipPath,
      // PositionProps
      bottom: bottom,
      float: float === 'start' || float === 'end' ? floatWithDirection : float,
      left: left || leftWithDirection,
      position: position,
      right: right || rightWithDirection,
      top: top,
      zIndex: zIndex,
      // SizeProps
      aspectRatio: aspectRatio,
      boxSizing: boxSizing,
      height: height,
      maxHeight: maxHeight,
      maxWidth: maxWidth,
      minHeight: minHeight,
      minWidth: minWidth,
      width: width,
      // TransformProps
      transform: transform,
      transition: transition,
      // ScrollSnapProps
      scrollSnapAlign: scrollSnapAlign,
      scrollSnapStop: scrollSnapStop,
      scrollSnapType: scrollSnapType,
      textDecoration: textDecoration
    }, getSpacing(props, direction, theme.spacing))
  };
};

/**
 * Box is a customizable styled div container that is capable of applying CSS styles while complying with Design Language.
 */
var BoxOriginal = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var direction = useDirectionContext();
  var theme = useThemeContext();
  var alignContent = props.alignContent,
    alignItems = props.alignItems,
    alignSelf = props.alignSelf,
    aspectRatio = props.aspectRatio,
    backgroundColor = props.backgroundColor,
    backgroundImage = props.backgroundImage,
    backgroundPosition = props.backgroundPosition,
    backgroundRepeat = props.backgroundRepeat,
    backgroundSize = props.backgroundSize,
    borderColor = props.borderColor,
    borderRadius = props.borderRadius,
    borderStyle = props.borderStyle,
    borderWidth = props.borderWidth,
    bottom = props.bottom,
    boxShadow = props.boxShadow,
    boxSizing = props.boxSizing,
    clipPath = props.clipPath,
    cursor = props.cursor,
    display = props.display,
    end = props.end,
    filter = props.filter,
    flex = props.flex,
    flexBasis = props.flexBasis,
    flexDirection = props.flexDirection,
    flexGrow = props.flexGrow,
    flexShrink = props.flexShrink,
    flexWrap = props.flexWrap,
    float = props.float,
    gap = props.gap,
    rowGap = props.rowGap,
    columnGap = props.columnGap,
    DEPRECATED_gap = props.DEPRECATED_gap,
    gridAutoColumns = props.gridAutoColumns,
    gridAutoFlow = props.gridAutoFlow,
    gridAutoRows = props.gridAutoRows,
    height = props.height,
    justifyContent = props.justifyContent,
    left = props.left,
    listStyle = props.listStyle,
    maskImage = props.maskImage,
    maxHeight = props.maxHeight,
    maxWidth = props.maxWidth,
    minHeight = props.minHeight,
    minWidth = props.minWidth,
    opacity = props.opacity,
    order = props.order,
    outlineColor = props.outlineColor,
    outlineStyle = props.outlineStyle,
    overflow = props.overflow,
    overflowScrolling = props.overflowScrolling,
    overflowX = props.overflowX,
    overflowY = props.overflowY,
    pointerEvents = props.pointerEvents,
    position = props.position,
    right = props.right,
    scrollSnapAlign = props.scrollSnapAlign,
    scrollSnapStop = props.scrollSnapStop,
    scrollSnapType = props.scrollSnapType,
    start = props.start,
    textAlign = props.textAlign,
    textColor = props.textColor,
    textDecoration = props.textDecoration,
    textOverflow = props.textOverflow,
    top = props.top,
    transform = props.transform,
    transition = props.transition,
    userSelect = props.userSelect,
    verticalAlign = props.verticalAlign,
    visibility = props.visibility,
    whiteSpace = props.whiteSpace,
    width = props.width,
    wordBreak = props.wordBreak,
    zIndex = props.zIndex,
    m = props.m,
    mb = props.mb,
    me = props.me,
    ms = props.ms,
    mt = props.mt,
    mx = props.mx,
    my = props.my,
    p = props.p,
    pb = props.pb,
    pe = props.pe,
    ps = props.ps,
    pt = props.pt,
    px = props.px,
    py = props.py,
    boxAs = props.as,
    children = props.children,
    disabled = props.disabled,
    empty = props.empty,
    hideScrollbar = props.hideScrollbar,
    interactive = props.interactive,
    interactiveChild = props.interactiveChild,
    interactiveWithin = props.interactiveWithin,
    safeArea = props.safeArea,
    type = props.type,
    value = props.value,
    rest = _objectWithoutProperties(props, _excluded);
  var backgroundToken = boxAs === 'button' && backgroundColor === undefined ? 'base.transparent' : backgroundColor;
  var borderWidthToken = boxAs === 'button' && borderWidth === undefined ? '0' : borderWidth;
  var cursorProp = boxAs === 'button' && cursor === undefined ? 'pointer' : cursor;
  var fontFamily = boxAs === 'button' ? 'inherit' : undefined;
  var cssProps = {
    alignContent: alignContent,
    alignItems: alignItems,
    alignSelf: alignSelf,
    aspectRatio: aspectRatio,
    backgroundColor: backgroundToken,
    backgroundImage: backgroundImage,
    backgroundPosition: backgroundPosition,
    backgroundRepeat: backgroundRepeat,
    backgroundSize: backgroundSize,
    borderColor: borderColor,
    borderRadius: borderRadius,
    borderStyle: borderStyle,
    borderWidth: borderWidthToken,
    bottom: bottom,
    boxShadow: boxShadow,
    boxSizing: boxSizing,
    clipPath: clipPath,
    cursor: cursorProp,
    display: display,
    end: end,
    filter: filter,
    flex: flex,
    flexBasis: flexBasis,
    flexDirection: flexDirection,
    flexGrow: flexGrow,
    flexShrink: flexShrink,
    flexWrap: flexWrap,
    float: float,
    fontFamily: fontFamily,
    gap: gap,
    rowGap: rowGap,
    columnGap: columnGap,
    DEPRECATED_gap: DEPRECATED_gap,
    gridAutoColumns: gridAutoColumns,
    gridAutoFlow: gridAutoFlow,
    gridAutoRows: gridAutoRows,
    height: height,
    justifyContent: justifyContent,
    left: left,
    listStyle: listStyle,
    maskImage: maskImage,
    maxHeight: maxHeight,
    maxWidth: maxWidth,
    minHeight: minHeight,
    minWidth: minWidth,
    opacity: opacity,
    order: order,
    outlineColor: outlineColor,
    outlineStyle: outlineStyle,
    overflow: overflow,
    overflowX: overflowX,
    overflowY: overflowY,
    pointerEvents: pointerEvents,
    position: position,
    right: right,
    scrollSnapAlign: scrollSnapAlign,
    scrollSnapStop: scrollSnapStop,
    scrollSnapType: scrollSnapType,
    start: start,
    textAlign: textAlign,
    textColor: textColor,
    textDecoration: textDecoration,
    textOverflow: textOverflow,
    top: top,
    transform: transform,
    transition: transition,
    userSelect: userSelect,
    verticalAlign: verticalAlign,
    visibility: visibility,
    whiteSpace: whiteSpace,
    width: width,
    wordBreak: wordBreak,
    zIndex: zIndex,
    m: m,
    mb: mb,
    me: me,
    ms: ms,
    mt: mt,
    mx: mx,
    my: my,
    p: p,
    pb: pb,
    pe: pe,
    ps: ps,
    pt: pt,
    px: px,
    py: py
  };
  var transformedProps = propTransform(cssProps, theme, direction);
  var styledQuery = toStyled(props, function (resultProps) {
    return propTransform(resultProps, theme, direction).$css;
  });

  // Note: This only works if we have background, border and text color
  var interactiveStateStyle = interactive && interactiveState(theme, interactive, position);
  var interactiveWithinStateStyle = interactiveWithin && interactiveWithinState(theme, interactiveWithin);
  var isInput = boxAs && ['input', 'textarea', 'select'].includes(boxAs);
  var isButton = boxAs === 'button';
  var inputType = isInput ? type : undefined;
  var buttonType = isButton ? type || 'button' : undefined;
  var inputValue = isInput || isButton ? value : undefined;
  return /*#__PURE__*/React.createElement(BoxStyled, _extends({}, transformedProps, {
    as: boxAs,
    type: buttonType || inputType,
    value: inputValue,
    disabled: disabled ? 'disabled' : undefined,
    $q: styledQuery,
    $interactiveState: interactiveStateStyle,
    $interactiveWithinState: interactiveWithinStateStyle,
    $hideScrollbar: hideScrollbar,
    $empty: empty,
    $safeArea: safeArea,
    $overflowScrolling: overflowScrolling,
    className: interactiveChild ? INTERACTIVE_WITHIN_CLASS : undefined,
    ref: ref
  }, rest), children);
});
BoxOriginal.displayName = 'Box';
BoxOriginal.defaultProps = {
  textColor: 'inherit'
};
var Box = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useExperimentContext = useExperimentContext(),
    cssArchitecture = _useExperimentContext.cssArchitecture;
  return cssArchitecture ? /*#__PURE__*/React.createElement(Box$1, _extends({}, props, {
    ref: ref
  })) : /*#__PURE__*/React.createElement(BoxOriginal, _extends({}, props, {
    ref: ref
  }));
});
Box.displayName = 'Box';
Box.defaultProps = {
  textColor: 'inherit'
};

export { Box };
//# sourceMappingURL=Box.js.map
